import { Play, Clock } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

interface VideoCardProps {
  id: string;
  title: string;
  description?: string;
  youtubeUrl: string;
  durationMinutes?: number;
  category: string;
  onPlay: (videoId: string, youtubeUrl: string) => void;
}

// Extrai o ID do vídeo do YouTube da URL
export function extractYoutubeId(url: string): string | null {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /^([a-zA-Z0-9_-]{11})$/ // Caso seja só o ID
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
}

export function VideoCard({ 
  id, 
  title, 
  description, 
  youtubeUrl, 
  durationMinutes,
  category,
  onPlay 
}: VideoCardProps) {
  const videoId = extractYoutubeId(youtubeUrl);
  const thumbnailUrl = videoId 
    ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`
    : null;

  const categoryLabels: Record<string, string> = {
    excel: "Excel",
    powerbi: "Power BI",
    sql: "SQL"
  };

  const categoryColors: Record<string, string> = {
    excel: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
    powerbi: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
    sql: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
  };

  return (
    <Card 
      className="group cursor-pointer overflow-hidden transition-all hover:shadow-lg hover:scale-[1.02]"
      onClick={() => onPlay(id, youtubeUrl)}
    >
      <div className="relative aspect-video overflow-hidden bg-muted">
        {thumbnailUrl ? (
          <img 
            src={thumbnailUrl} 
            alt={title}
            className="w-full h-full object-cover transition-transform group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-muted">
            <Play className="h-12 w-12 text-muted-foreground" />
          </div>
        )}
        
        {/* Overlay com botão de play */}
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <div className="w-16 h-16 rounded-full bg-primary/90 flex items-center justify-center">
            <Play className="h-8 w-8 text-primary-foreground ml-1" />
          </div>
        </div>

        {/* Duração */}
        {durationMinutes && (
          <div className="absolute bottom-2 right-2 bg-black/80 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {durationMinutes} min
          </div>
        )}
      </div>

      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <Badge className={categoryColors[category] || "bg-muted"}>
            {categoryLabels[category] || category}
          </Badge>
        </div>
        <h3 className="font-semibold text-foreground line-clamp-2 mb-1 group-hover:text-primary transition-colors">
          {title}
        </h3>
        {description && (
          <p className="text-sm text-muted-foreground line-clamp-2">
            {description}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
